import { css } from '@emotion/css';
import { Accordion, Button, Divider, Group, Portal, Select, Stack, Text } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { ClientHoverCard } from 'components/ClientHoverCard';
import { Panel } from 'components/Panel';
import dayjs from 'dayjs';
import html2pdf from 'html2pdf.js';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, tKeys, tKeysLoose, useTranslation } from 'locales';
import { useRef, useState } from 'react';
import { entries, flatMap, isDefined, isEmpty, isNonNullish, pipe, sort } from 'remeda';
import HTML from 'shared/components/HTML/HTML';
import type { Reminder } from '../api';
import { ReminderDetailItem } from './ReminderDetailItem';
import { StatusCard } from './StatusCard';

type EmailTabProps = {
  reminder: Reminder;
};

export const EmailTab = ({ reminder }: EmailTabProps) => {
  const { t } = useTranslation();

  const company = useCompanyConfiguration();
  const canUseEmailStatus = company.package.can_use_emails_activity;

  const [selectedRecipientIndex, setSelectedRecipientIndex] = useState('0');
  const selectedRecipient = reminder.emails[Number(selectedRecipientIndex)];
  const recipientOptions = reminder.emails.map((recipient, index) => ({
    value: String(index),
    label:
      reminder.emails.filter(({ email }) => email === recipient.email).length > 1
        ? `${recipient.email} — ${dayjs(recipient.sentAt).format('L LT')}`
        : recipient.email,
  }));

  const emailBodyRef = useRef<HTMLDivElement>(null);

  if (!isDefined(selectedRecipient))
    return (
      <Text c="dimmed" fz="sm">
        Error: No recipients found for the selected email.
      </Text>
    );

  const handleClickDownload = async () => {
    const emailBodyElement = emailBodyRef.current;
    if (!emailBodyElement) return;

    const documentName = `${reminder.debtorFullName}#${reminder.id} - ${reminder.emails[0].subject} - ${reminder.sentAt.format('LLL')}.pdf`;

    await html2pdf()
      .set({
        margin: 12,
        html2canvas: {
          // This class which we add on the DOM clone that html2canvas generates activates the print-only styles
          onclone: (clonedDoc: Document) => {
            clonedDoc.body.classList.add('pdf-export');
          },
        },
        jsPDF: {
          unit: 'px',
          format: [emailBodyElement.scrollWidth, emailBodyElement.scrollHeight],
          orientation: 'portrait',
        },
      })
      .from(emailBodyElement)
      .save(documentName);
  };

  const statusesForRecipient = processStatuses(selectedRecipient.statuses);

  return (
    <Panel>
      <div ref={emailBodyRef}>
        <Group align="start" justify="space-between">
          <Stack gap="lg">
            <Portal target="#reminder-download-button-portal">
              <Button
                onClick={handleClickDownload}
                leftSection={<IconDownload stroke={1.5} size={18} />}
              >
                {t(tKeys.INVOICE.DOWNLOAD_PDF)}
              </Button>
            </Portal>
            <ReminderDetailItem
              label={t(i18nKeys.REMINDER.CLIENT)}
              value={
                <ClientHoverCard
                  debtorId={reminder.debtorId}
                  debtorName={reminder.debtorFullName}
                />
              }
            />
            <div className={styles.noPrint}>
              <ReminderDetailItem
                label={t(i18nKeys.REMINDER.SENT_TO)}
                value={
                  <Select
                    w={300}
                    size="sm"
                    data={recipientOptions}
                    value={selectedRecipientIndex}
                    onChange={v => {
                      if (isNonNullish(v)) setSelectedRecipientIndex(v);
                    }}
                    allowDeselect={false}
                  />
                }
              />
            </div>
            <div className={styles.printOnly}>
              <ReminderDetailItem
                label={t(i18nKeys.REMINDER.SENT_TO)}
                value={selectedRecipient.email}
              />
            </div>
            <div className={styles.noPrint}>
              {!isEmpty(statusesForRecipient) && canUseEmailStatus && (
                <ReminderDetailItem
                  label={t(i18nKeys.REMINDER.DELIVERY_STATUS)}
                  value={
                    <Accordion fz={12} variant="separated" w={300}>
                      <Accordion.Item value="1">
                        <Accordion.Control fz={14} c="dimmed" h={36}>
                          {t(i18nKeys.REMINDER.SEE_DELIVERY_HISTORY)}
                        </Accordion.Control>
                        <Accordion.Panel>
                          {statusesForRecipient.map(statusObject => (
                            <Group
                              key={statusObject.timestamp.toString()}
                              justify="space-between"
                              gap={0}
                              fz="0.8rem"
                              fw={700}
                              pt={10}
                              pl={5}
                            >
                              {t(tKeysLoose.MAILS.STATUS[statusObject.status.toUpperCase()])}
                              <span style={{ fontStyle: 'italic', color: '#bbb' }}>
                                {statusObject.timestamp.format('LLL')}
                              </span>
                            </Group>
                          ))}
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>
                  }
                />
              )}
            </div>
            <ReminderDetailItem
              label={t(i18nKeys.REMINDER.DATE_SENT)}
              value={<Text>{dayjs(selectedRecipient.sentAt).format('LLLL')}</Text>}
            />
            {selectedRecipient.subject && (
              <ReminderDetailItem
                label={t(i18nKeys.REMINDER.SUBJECT)}
                value={<Text>{selectedRecipient.subject}</Text>}
              />
            )}
          </Stack>
          <div className={styles.noPrint}>
            <StatusCard reminder={reminder} />
          </div>
        </Group>
        <Divider my="xl" />
        <HTML html={selectedRecipient.body} />
      </div>
    </Panel>
  );
};

function processStatuses(statuses: Reminder['emails'][number]['statuses']) {
  return pipe(
    statuses,
    entries(),
    flatMap(([status, timestamps]) =>
      timestamps.map(timestamp => ({ status, timestamp: dayjs(timestamp) })),
    ),
    sort((a, b) => b.timestamp.diff(a.timestamp)),
  );
}

const styles = {
  printOnly: css`
    display: none !important;
    body.pdf-export & {
      display: block !important;
    }
  `,

  noPrint: css`
    display: block !important;
    body.pdf-export & {
      display: none !important;
    }
  `,
};
